/**
 * Pure rules for prospect sample parties.
 *
 * Every sample party sits on one private Oscars show (kind 'sample') that
 * stores every winner. Each party keeps its own progress: how many categories
 * it has "announced", in ceremony order. Winners past that point stay hidden
 * for that party. These helpers never describe a write to the show or to a
 * party that is not a sample.
 */

export const SAMPLE_SHOW_STATES = ["pre", "halfway", "final"] as const;

export type SampleShowState = (typeof SAMPLE_SHOW_STATES)[number];

export const AWARD_SHOW_KINDS = ["real", "sample"] as const;

export type AwardShowKind = (typeof AWARD_SHOW_KINDS)[number];

/** The single private Oscars show behind every sample party. */
export const SAMPLE_SHOW_ID = "de5a1e5d-0001-4000-8000-000000000013";

/** A pre-show sample party keeps voting open this long from the switch. */
export const SAMPLE_PRE_VOTING_WINDOW_MS = 365 * 24 * 60 * 60 * 1000;

/** Default clone source: the Roku sales party. */
export const DEFAULT_SAMPLE_TEMPLATE_PARTY_ID =
  "de5a1e5d-0002-4000-8000-000000000001";

/** Nielsen's existing sales party. The sample backfill includes this id. */
export const NIELSEN_SAMPLE_PARTY_ID =
  "de5a1e5d-0002-4000-8000-000000000004";

/** The public 2026 Oscars show. Sample tools must not retarget or edit it. */
export const REAL_SHARED_ACADEMY_AWARDS_2026_ID =
  "3ea12424-6ff2-4dc1-a0cd-acae05a80d54";

export const SAMPLE_EMAIL_DOMAIN = "demo.awardparty.invalid";

export const SAMPLE_MEMBER_COUNT_MAX = 120;

export const SAMPLE_PARTY_GUARD_CODES = [
  "not_sample",
  "missing_party",
  "template_not_sample",
  "protected_show",
  "missing_show",
  "template_off_sample_show",
  "invalid_company",
  "invalid_branding",
  "missing_branding",
  "brand_host_blocked",
  "image_required",
  "member_required",
  "missing_member",
  "owner_member",
] as const;

export type SamplePartyGuardCode = (typeof SAMPLE_PARTY_GUARD_CODES)[number];

export class SamplePartyGuardError extends Error {
  readonly code: SamplePartyGuardCode;

  constructor(code: SamplePartyGuardCode) {
    super(code);
    this.name = "SamplePartyGuardError";
    this.code = code;
  }
}

export function isSamplePartyGuardError(
  error: unknown,
): error is SamplePartyGuardError {
  return error instanceof SamplePartyGuardError;
}

export type SamplePartyLinks = {
  invite: string;
  party: string;
  tv: string | null;
  recap: string | null;
  report: string | null;
};

export type SamplePartySummary = {
  id: string;
  name: string;
  brandName: string;
  logoUrl: string | null;
  showState: SampleShowState | "other";
  /** Categories this party has announced, in ceremony order. */
  announcedCount: number;
  /** Categories with a stored winner on the sample show. */
  announceableCount: number;
  showName: string;
  memberCount: number;
  /** Real accounts in the party. A clean sample has none. */
  realMembers: { userId: string; email: string }[];
  links: SamplePartyLinks;
};

export type SamplePartyCreateResult = SamplePartySummary & {
  reused: boolean;
};

export type SampleMemberRef = {
  userId: string;
  email: string;
  clerkId: string;
  role: "OWNER" | "MEMBER";
};

export function isSampleShowState(value: string): value is SampleShowState {
  return (SAMPLE_SHOW_STATES as readonly string[]).includes(value);
}

export function isAwardShowKind(value: string): value is AwardShowKind {
  return (AWARD_SHOW_KINDS as readonly string[]).includes(value);
}

/** Sample shows hold demo data and never go public. */
export function canPublishAwardShowKind(kind: AwardShowKind): boolean {
  return kind !== "sample";
}

export function isHistoricalSamplePartyId(partyId: string): boolean {
  return partyId.trim().toLowerCase().startsWith("de5a1e5d-0002-");
}

export type SampleProgressCategory = {
  id: string;
  orderIndex: number;
  /** Earliest stored winner time, or null when the category has no winner. */
  firstAnnouncedAt: Date | string | null;
};

function instantMs(value: Date | string): number {
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * Ceremony order for a sample show: categories with a stored winner, by the
 * time that winner was announced, then by ballot order.
 */
export function sampleAnnouncementOrder(
  categories: readonly SampleProgressCategory[],
): string[] {
  return categories
    .flatMap((category) =>
      category.firstAnnouncedAt === null
        ? []
        : [{ ...category, at: instantMs(category.firstAnnouncedAt) }],
    )
    .sort((left, right) => left.at - right.at || left.orderIndex - right.orderIndex)
    .map((category) => category.id);
}

/** Null count means the party follows every stored winner. */
export function sampleAnnouncedCategoryIds(
  categories: readonly SampleProgressCategory[],
  announcedCount: number | null,
): Set<string> {
  const order = sampleAnnouncementOrder(categories);
  if (announcedCount === null) return new Set(order);
  return new Set(order.slice(0, normalizeSampleAnnouncedCount(announcedCount, order.length)));
}

/**
 * Drops winners for categories the party has not announced yet. A null
 * filter is a real party and keeps every result.
 */
export function keepAnnouncedResults<
  C extends { id: string; results: readonly unknown[] },
>(categories: readonly C[], announced: ReadonlySet<string> | null): C[] {
  if (announced === null) return [...categories];
  return categories.map((category) =>
    announced.has(category.id) || category.results.length === 0
      ? category
      : { ...category, results: [] },
  );
}

export function normalizeSampleAnnouncedCount(
  count: number,
  announceableCount: number,
): number {
  if (!Number.isFinite(count)) return 0;
  return Math.min(Math.max(0, announceableCount), Math.max(0, Math.floor(count)));
}

export function sampleAnnouncedCountForState(
  state: SampleShowState,
  announceableCount: number,
): number {
  const total = Math.max(0, Math.floor(announceableCount));
  if (state === "pre") return 0;
  if (state === "halfway") return Math.floor(total / 2);
  return total;
}

export function sampleShowStateFromProgress(
  announcedCount: number | null,
  announceableCount: number,
): SampleShowState {
  if (announcedCount === null) return "final";
  if (announcedCount <= 0) return "pre";
  if (announcedCount >= announceableCount) return "final";
  return "halfway";
}

export type SampleProgressTarget =
  | { state: SampleShowState }
  | { announcedCount: number };

export type SampleProgressPlan = {
  announcedCount: number;
  isManuallyLocked: boolean;
  votingClosesAt: Date;
};

/**
 * Progress write for one sample party. Before the show, voting is open with a
 * deadline in the future. Once a category is announced, the ballot locks.
 */
export function planSampleProgress(input: {
  isSample: boolean;
  awardShowKind: AwardShowKind;
  announceableCount: number;
  currentVotingClosesAt: Date | null;
  target: SampleProgressTarget;
  now: Date;
}): SampleProgressPlan {
  if (!input.isSample) throw new SamplePartyGuardError("not_sample");
  if (input.awardShowKind !== "sample") {
    throw new SamplePartyGuardError("protected_show");
  }
  const announcedCount =
    "state" in input.target
      ? sampleAnnouncedCountForState(input.target.state, input.announceableCount)
      : normalizeSampleAnnouncedCount(
          input.target.announcedCount,
          input.announceableCount,
        );
  const current = input.currentVotingClosesAt;
  const keepsFutureDeadline =
    current !== null && current.getTime() > input.now.getTime();
  if (announcedCount === 0) {
    return {
      announcedCount,
      isManuallyLocked: false,
      votingClosesAt: keepsFutureDeadline
        ? current
        : new Date(input.now.getTime() + SAMPLE_PRE_VOTING_WINDOW_MS),
    };
  }
  return {
    announcedCount,
    isManuallyLocked: true,
    votingClosesAt: current ?? input.now,
  };
}

/**
 * The show start a sample party behaves as if it had. Before the show the
 * single sample show has already aired, so the party uses its own voting
 * deadline instead.
 */
export function sampleEffectiveShowStart<T extends Date | string | null>(input: {
  isSample: boolean;
  sampleAnnouncedCount: number | null;
  votingClosesAt: T;
  showStartTime: T;
}): T {
  if (!input.isSample || input.sampleAnnouncedCount !== 0) {
    return input.showStartTime;
  }
  return input.votingClosesAt ?? input.showStartTime;
}

/** Party-row form of sampleEffectiveShowStart. */
export function partyShowStartTime(
  party: {
    isSample?: boolean | null;
    sampleAnnouncedCount?: number | null;
    votingClosesAt: Date | null;
  },
  showStartTime: Date | null | undefined,
): Date | null {
  return sampleEffectiveShowStart<Date | null>({
    isSample: party.isSample === true,
    sampleAnnouncedCount: party.sampleAnnouncedCount ?? null,
    votingClosesAt: party.votingClosesAt,
    showStartTime: showStartTime ?? null,
  });
}

export function isSampleFakeClerkId(clerkId: string): boolean {
  return (
    clerkId.startsWith("sample_") || clerkId.startsWith("seed_demo_brand_")
  );
}

export function assertSampleTemplate(
  party: { isSample: boolean } | null | undefined,
): void {
  if (!party) throw new SamplePartyGuardError("missing_party");
  if (!party.isSample) throw new SamplePartyGuardError("template_not_sample");
}

export function assertDeletableSample(
  party: { isSample: boolean } | null | undefined,
): void {
  if (!party) throw new SamplePartyGuardError("missing_party");
  if (!party.isSample) throw new SamplePartyGuardError("not_sample");
}

export const SAMPLE_FACE_AVATARS: readonly string[] = [
  "/branding-demo/roku/people/roku-host.webp",
  ...Array.from(
    { length: 43 },
    (_, index) => `/branding-demo/roku/people/roku-${index + 1}.webp`,
  ),
];

export type SampleCompanyInput = {
  name: string;
  domain: string;
};

export type TemplateSampleMember = {
  id: string;
  name: string | null;
  email: string;
  imageUrl: string | null;
  role: "OWNER" | "MEMBER";
  clerkId: string;
  joinedAt: string;
};

export type PlannedSampleMember = {
  sourceUserId: string;
  voteSourceUserId: string;
  name: string;
  email: string;
  imageUrl: string;
  role: "OWNER" | "MEMBER";
  displayCompany: string;
  joinedAt: string;
};

export function sampleEmailSlug(companyName: string): string {
  const slug = companyName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
  return slug || "sample";
}

export function normalizeSampleCompanyName(value: string): string | null {
  const trimmed = value.trim().replace(/\s+/g, " ");
  if (!trimmed) return null;
  return trimmed.slice(0, 80);
}

function isTemplateFakeMember(member: TemplateSampleMember): boolean {
  return (
    isSampleFakeClerkId(member.clerkId) ||
    member.email.toLowerCase().endsWith(".invalid")
  );
}

export function planSampleMembers(input: {
  templateMembers: readonly TemplateSampleMember[];
  companies: readonly SampleCompanyInput[];
  memberCount?: number;
  slug: string;
}): PlannedSampleMember[] {
  const companies = input.companies
    .map((company) => normalizeSampleCompanyName(company.name))
    .filter((name): name is string => Boolean(name));
  if (companies.length === 0) {
    throw new SamplePartyGuardError("invalid_company");
  }

  const fakes = input.templateMembers.filter(isTemplateFakeMember);
  const ordered = [...fakes].sort((left, right) => {
    if (left.role === right.role) return 0;
    return left.role === "OWNER" ? -1 : 1;
  });
  if (ordered.length === 0) {
    throw new SamplePartyGuardError("template_not_sample");
  }

  const requested = input.memberCount ?? ordered.length;
  const count = Math.min(
    SAMPLE_MEMBER_COUNT_MAX,
    Math.max(1, Math.floor(requested)),
  );

  return Array.from({ length: count }, (_, index) => {
    const source = ordered[index % ordered.length];
    if (!source) throw new SamplePartyGuardError("template_not_sample");
    const company = companies[index % companies.length] ?? companies[0];
    if (!company) throw new SamplePartyGuardError("invalid_company");
    const local = index === 0 ? "host" : String(index);
    const templateFace = source.imageUrl?.startsWith(
      "/branding-demo/roku/people/",
    )
      ? source.imageUrl
      : null;
    return {
      sourceUserId: source.id,
      voteSourceUserId: source.id,
      name: source.name?.trim() || `Guest ${index + 1}`,
      email: `${input.slug}-${local}@${SAMPLE_EMAIL_DOMAIN}`,
      imageUrl:
        templateFace ??
        SAMPLE_FACE_AVATARS[index % SAMPLE_FACE_AVATARS.length] ??
        SAMPLE_FACE_AVATARS[0] ??
        "/branding-demo/roku/people/roku-host.webp",
      role: index === 0 ? "OWNER" : "MEMBER",
      displayCompany: company,
      joinedAt: source.joinedAt,
    };
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function rewriteSampleText(
  text: string,
  replacements: readonly { from: string; to: string }[],
): string {
  const usable = replacements
    .map((item) => ({ from: item.from.trim(), to: item.to.trim() }))
    .filter(
      (item) =>
        item.from.length >= 2 &&
        item.to.length > 0 &&
        item.from.toLowerCase() !== item.to.toLowerCase(),
    )
    .sort((left, right) => right.from.length - left.from.length);
  let next = text;
  for (const item of usable) {
    next = next.replace(new RegExp(escapeRegExp(item.from), "gi"), item.to);
  }
  return next;
}

const GENERIC_SAMPLE_CHAT = [
  "Locked in my ballot.",
  "This category is going to be close.",
  "I changed my pick.",
  "The room is split on this one.",
  "Waiting on the envelope.",
  "That result shuffled the board.",
] as const;

export function genericSampleChatLine(seed: string): string {
  let hash = 0;
  for (const char of seed) {
    hash = (hash + char.charCodeAt(0)) >>> 0;
  }
  return (
    GENERIC_SAMPLE_CHAT[hash % GENERIC_SAMPLE_CHAT.length] ??
    GENERIC_SAMPLE_CHAT[0]
  );
}

export function rewriteSampleChatLine(
  body: string,
  sourceNames: readonly string[],
  targetName: string,
): string {
  const rewritten = rewriteSampleText(
    body,
    sourceNames.map((from) => ({ from, to: targetName })),
  );
  const leftover = sourceNames.some((name) => {
    const token = name.trim();
    if (token.length < 3) return false;
    if (targetName.toLowerCase().includes(token.toLowerCase())) return false;
    return rewritten.toLowerCase().includes(token.toLowerCase());
  });
  if (leftover || !rewritten.trim()) return genericSampleChatLine(body);
  return rewritten;
}

export function samplePartyName(
  sourceName: string,
  sourceBrand: string,
  targetBrand: string,
): string {
  const brand = targetBrand.trim() || "Sample";
  if (
    sourceBrand.trim() &&
    sourceName.toLowerCase().includes(sourceBrand.trim().toLowerCase())
  ) {
    return rewriteSampleText(sourceName, [
      { from: sourceBrand, to: brand },
    ]).slice(0, 120);
  }
  return `${brand} Oscars Night`.slice(0, 120);
}

/** Members that are real accounts, not cloned demo guests. */
export function sampleRealMembers<T extends { email: string; clerkId: string }>(
  members: readonly T[],
): T[] {
  return members.filter(
    (member) =>
      !isSampleFakeClerkId(member.clerkId) &&
      !member.email.toLowerCase().endsWith(".invalid"),
  );
}

/**
 * Picks the sample party member to remove by user id or email. The host
 * stays, because the party, its ballots, and the report hang off them.
 */
export function resolveSampleMemberToRemove<T extends SampleMemberRef>(input: {
  isSample: boolean;
  ownerId: string;
  members: readonly T[];
  userId?: string;
  email?: string;
}): T {
  if (!input.isSample) throw new SamplePartyGuardError("not_sample");
  const userId = input.userId?.trim() ?? "";
  const email = input.email?.trim().toLowerCase() ?? "";
  if (!userId && !email) throw new SamplePartyGuardError("member_required");
  const member = input.members.find((candidate) =>
    userId
      ? candidate.userId === userId
      : candidate.email.trim().toLowerCase() === email,
  );
  if (!member) throw new SamplePartyGuardError("missing_member");
  if (member.role === "OWNER" || member.userId === input.ownerId) {
    throw new SamplePartyGuardError("owner_member");
  }
  return member;
}

export function sampleFakeUserIdsToDelete(
  members: readonly {
    id: string;
    clerkId: string;
    stillParticipating: boolean;
    stillOwnsParty: boolean;
  }[],
): string[] {
  return members
    .filter(
      (member) =>
        isSampleFakeClerkId(member.clerkId) &&
        !member.stillParticipating &&
        !member.stillOwnsParty,
    )
    .map((member) => member.id);
}

export function buildSamplePartyLinks(
  origin: string,
  input: {
    partyId: string;
    inviteCode: string;
    tvDisplayToken: string | null;
    recapShareToken: string | null;
    reportToken: string | null;
    reportPublished: boolean;
  },
): SamplePartyLinks {
  const base = origin.replace(/\/$/, "");
  return {
    invite: `${base}/join/${input.inviteCode}`,
    party: `${base}/parties/${input.partyId}`,
    tv: input.tvDisplayToken ? `${base}/tv/${input.tvDisplayToken}` : null,
    recap: input.recapShareToken
      ? `${base}/recaps/${input.recapShareToken}`
      : null,
    report:
      input.reportPublished && input.reportToken
        ? `${base}/reports/${input.reportToken}`
        : null,
  };
}

export type ParsedBrand = {
  logoUrl: string | null;
  iconUrl: string | null;
  themeColor: string | null;
};

function metaContent(html: string, key: string): string | null {
  const patterns = [
    new RegExp(
      `<meta[^>]+(?:property|name)=["']${key}["'][^>]+content=["']([^"']+)["']`,
      "i",
    ),
    new RegExp(
      `<meta[^>]+content=["']([^"']+)["'][^>]+(?:property|name)=["']${key}["']`,
      "i",
    ),
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(html);
    const value = match?.[1]?.trim();
    if (value) return value;
  }
  return null;
}

function linkHref(html: string, rel: string): string | null {
  const pattern = new RegExp(
    `<link[^>]+rel=["'][^"']*\\b${rel}\\b[^"']*["'][^>]+href=["']([^"']+)["']`,
    "i",
  );
  const swapped = new RegExp(
    `<link[^>]+href=["']([^"']+)["'][^>]+rel=["'][^"']*\\b${rel}\\b[^"']*["']`,
    "i",
  );
  return pattern.exec(html)?.[1]?.trim() || swapped.exec(html)?.[1]?.trim() || null;
}

function resolveBrandUrl(value: string | null, pageUrl: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, pageUrl);
    if (url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function parseBrandFromHtml(html: string, pageUrl: string): ParsedBrand {
  const theme = metaContent(html, "theme-color");
  const themeColor = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(theme ?? "")
    ? theme
    : null;
  return {
    logoUrl: resolveBrandUrl(
      metaContent(html, "og:image") ?? metaContent(html, "twitter:image"),
      pageUrl,
    ),
    iconUrl: resolveBrandUrl(
      linkHref(html, "apple-touch-icon") ??
        linkHref(html, "icon") ??
        linkHref(html, "shortcut"),
      pageUrl,
    ),
    themeColor,
  };
}

function isBlockedBrandHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  if (
    host === "localhost" ||
    host.endsWith(".local") ||
    host.endsWith(".internal") ||
    host === "metadata.google.internal"
  ) {
    return true;
  }
  const ipv4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!ipv4) return false;
  const a = Number(ipv4[1]);
  const b = Number(ipv4[2]);
  if (a === 10 || a === 127 || a === 0) return true;
  if (a === 169 && b === 254) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return false;
}

export function assertPublicBrandUrl(raw: string): URL {
  let url: URL;
  try {
    url = new URL(raw.includes("://") ? raw : `https://${raw}`);
  } catch {
    throw new SamplePartyGuardError("brand_host_blocked");
  }
  if (url.protocol !== "https:") {
    throw new SamplePartyGuardError("brand_host_blocked");
  }
  if (isBlockedBrandHost(url.hostname)) {
    throw new SamplePartyGuardError("brand_host_blocked");
  }
  return url;
}

export function rewriteSampleJsonStrings<T>(
  value: T,
  replacements: readonly { from: string; to: string }[],
): T {
  if (typeof value === "string") {
    return rewriteSampleText(value, replacements) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) =>
      rewriteSampleJsonStrings(item, replacements),
    ) as T;
  }
  if (value && typeof value === "object") {
    const next: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      next[key] = rewriteSampleJsonStrings(item, replacements);
    }
    return next as T;
  }
  return value;
}
